import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  LuCircleCheck, LuTarget, LuUsers, LuAward, LuRocket,
  LuHeart, LuShield, LuTrendingUp, LuGlobe, LuCode,
  LuArrowRight, LuQuote
} from 'react-icons/lu';

const values = [
  {
    icon: LuCircleCheck,
    title: 'Engineering Excellence',
    description: 'We prioritize clean code, scalable architecture, and maintainable systems over quick fixes.',
    color: 'blue'
  },
  {
    icon: LuAward,
    title: 'Academic Rigor',
    description: 'Built by engineers with strong academic foundations and a research-oriented mindset.',
    color: 'orange'
  },
  {
    icon: LuTarget,
    title: 'Long-term Vision',
    description: 'We build solutions that grow with your business, not temporary patches.',
    color: 'blue'
  },
  {
    icon: LuUsers,
    title: 'Collaborative Approach',
    description: 'Working closely with clients to understand needs and deliver tailored solutions.',
    color: 'orange'
  },
  {
    icon: LuHeart,
    title: 'Client-Centric Focus',
    description: 'Your success is our success. We measure our achievements through your growth.',
    color: 'blue'
  },
  {
    icon: LuShield,
    title: 'Trust & Transparency',
    description: 'Honest communication and clear deliverables at every stage of the project.',
    color: 'orange'
  }
];

const stats = [
  { number: '50+', label: 'Projects Delivered', icon: LuRocket },
  { number: '30+', label: 'Happy Clients', icon: LuUsers },
  { number: '10+', label: 'Technologies', icon: LuCode },
  { number: '5+', label: 'Years Experience', icon: LuTrendingUp }
];

const team = [
  {
    name: 'Expert Engineers',
    role: 'Full-Stack Development',
    description: 'Our engineering team brings expertise in modern frameworks, cloud architecture, and scalable systems.',
    skills: ['React', 'Node.js', 'Python', 'AWS', 'DevOps']
  },
  {
    name: 'AI Specialists',
    role: 'Machine Learning & AI',
    description: 'Dedicated AI experts who transform complex problems into intelligent automation solutions.',
    skills: ['TensorFlow', 'OpenAI', 'LangChain', 'NLP', 'Computer Vision']
  },
  {
    name: 'Digital Marketers',
    role: 'Growth & Marketing',
    description: 'Data-driven marketers who understand the intersection of technology and business growth.',
    skills: ['SEO', 'PPC', 'Content Strategy', 'Analytics', 'Social Media']
  }
];

const milestones = [
  { year: '2019', title: 'Founded', description: 'Started with a vision to bridge technology and business growth' },
  { year: '2020', title: 'First Major Client', description: 'Delivered enterprise-grade solutions for our first Fortune 500 client' },
  { year: '2021', title: 'AI Integration', description: 'Expanded into AI/ML services, helping businesses automate intelligently' },
  { year: '2022', title: 'Digital Marketing', description: 'Added full-service digital marketing to our offerings' },
  { year: '2023', title: 'Global Reach', description: 'Expanded operations to serve clients across 10+ countries' },
  { year: '2024', title: 'Innovation Hub', description: 'Launched R&D division focusing on emerging technologies' }
];

export default function About() {
  return (
    <div className="bg-slate-950" data-testid="about-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0">
          <motion.div
            animate={{ scale: [1, 1.2, 1], x: [0, 30, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-20 left-10 w-[500px] h-[500px] bg-blue-600/15 rounded-full blur-3xl"
          />
          <motion.div
            animate={{ scale: [1, 1.3, 1], x: [0, -30, 0] }}
            transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
            className="absolute bottom-20 right-10 w-[600px] h-[600px] bg-orange-600/15 rounded-full blur-3xl"
          />
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center space-y-6"
          >
            <span className="inline-flex items-center gap-2 text-sm font-medium text-blue-400 uppercase tracking-widest">
              <span className="w-8 h-px bg-blue-400"></span>
              About TechMeetsU
              <span className="w-8 h-px bg-blue-400"></span>
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight">
              Where Technology Meets{' '}
              <span className="text-gradient-orange">Your Growth</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
              We're not just developers — we're your strategic technology partners committed to transforming
              your vision into reality with engineering excellence and innovative solutions.
            </p>
          </motion.div>
        </div>
      </section>



      {/* Story Section */}
      <section className="py-20 md:py-28" data-testid="story-section">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <span className="text-sm font-medium text-orange-400 uppercase tracking-widest">Our Story</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">
                From Vision to <span className="text-gradient-blue">Innovation</span>
              </h2>
              <div className="space-y-4 text-slate-300 leading-relaxed">
                <p>
                  TechMeetsU was founded with a simple yet powerful belief: technology should empower businesses,
                  not complicate them. We saw too many companies struggling with software that couldn't scale,
                  marketing that didn't convert, and digital presence that failed to engage.
                </p>
                <p>
                  Our founders, experienced engineers and digital strategists, came together to build something
                  different — a technology partner that understands both the technical depth required for
                  world-class software and the business acumen needed for real growth.
                </p>
                <p>
                  Today, we serve clients across the globe, from ambitious startups to established enterprises,
                  all united by one goal: leveraging technology for sustainable growth.
                </p>
              </div>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 font-semibold transition-colors group"
              >
                Explore Our Services
                <LuArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-slate-800">
                    <img
                      src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&h=500&fit=crop"
                      alt="Team collaboration"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="aspect-square rounded-2xl bg-gradient-to-br from-blue-600/20 to-blue-800/20 border border-blue-500/20 p-6 flex flex-col justify-center">
                    <LuQuote size={32} className="text-blue-400 mb-3" />
                    <p className="text-white text-sm font-medium italic">
                      "Innovation distinguishes between a leader and a follower."
                    </p>
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="aspect-square rounded-2xl bg-gradient-to-br from-orange-600/20 to-orange-800/20 border border-orange-500/20 p-6 flex flex-col justify-center">
                    <div className="text-4xl font-heading font-bold text-orange-400 mb-2">100%</div>
                    <p className="text-slate-300 text-sm">Client Satisfaction Rate</p>
                  </div>
                  <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-slate-800">
                    <img
                      src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=400&h=500&fit=crop"
                      alt="Modern workspace"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-blue-600/20 rounded-full blur-2xl" />
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-orange-600/20 rounded-full blur-2xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-slate-900/50 to-slate-950" data-testid="vision-mission">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-slate-900/50 border border-blue-500/20 rounded-3xl p-8 md:p-10 hover:border-blue-500/40 transition-all group"
            >
              <div className="w-14 h-14 rounded-2xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <LuGlobe size={28} className="text-blue-400" />
              </div>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">Our Vision</h3>
              <p className="text-slate-300 leading-relaxed">
                To be the most trusted technology partner for businesses worldwide, known for delivering
                innovative solutions that drive measurable growth. We envision a future where every business,
                regardless of size, can harness the power of cutting-edge technology to achieve their goals.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="bg-slate-900/50 border border-orange-500/20 rounded-3xl p-8 md:p-10 hover:border-orange-500/40 transition-all group"
            >
              <div className="w-14 h-14 rounded-2xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <LuRocket size={28} className="text-orange-400" />
              </div>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">Our Mission</h3>
              <p className="text-slate-300 leading-relaxed">
                To empower businesses with comprehensive technology solutions — from custom software and
                AI integration to digital marketing and brand building. We combine engineering excellence
                with strategic thinking to deliver solutions that not only meet today's needs but anticipate tomorrow's challenges.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-28" data-testid="values-section">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium text-blue-400 uppercase tracking-widest">Our Values</span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mt-4">
              Principles That Guide <span className="text-gradient-orange">Every Project</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              const isOrange = value.color === 'orange';
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className={`bg-slate-900/50 border rounded-2xl p-6 md:p-8 transition-all group ${isOrange
                    ? 'border-orange-500/20 hover:border-orange-500/50'
                    : 'border-blue-500/20 hover:border-blue-500/50'
                    }`}
                  data-testid={`value-card-${index}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-transform group-hover:scale-110 ${isOrange
                    ? 'bg-orange-500/10 border border-orange-500/30'
                    : 'bg-blue-500/10 border border-blue-500/30'
                    }`}>
                    <Icon size={24} className={isOrange ? 'text-orange-400' : 'text-blue-400'} />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-white mb-3">{value.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Expertise Section */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-slate-900/30 to-slate-950" data-testid="team-section">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium text-orange-400 uppercase tracking-widest">Our Team</span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mt-4">
              Expertise That <span className="text-gradient-blue">Delivers Results</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {team.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 md:p-8 hover:border-orange-500/30 transition-all group"
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600/20 to-orange-600/20 border border-slate-700 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <LuUsers size={28} className="text-orange-400" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-1">{member.name}</h3>
                <p className="text-orange-400 text-sm font-medium mb-4">{member.role}</p>
                <p className="text-slate-400 text-sm leading-relaxed mb-5">{member.description}</p>
                <div className="flex flex-wrap gap-2">
                  {member.skills.map((skill) => (
                    <span
                      key={skill}
                      className="px-3 py-1 bg-slate-800/50 border border-slate-700 rounded-full text-xs text-slate-300"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>



      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-slate-900/30 via-orange-950/10 to-slate-900/30" data-testid="about-cta">
        <div className="container mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center space-y-6"
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white">
              Ready to Work with <span className="text-gradient-orange">Us?</span>
            </h2>
            <p className="text-lg text-slate-300">
              Let's discuss how we can help transform your business with technology
            </p>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-500 hover:to-orange-600 text-white px-10 py-4 rounded-full text-lg font-bold transition-all shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 group"
              >
                <span>Start a Conversation</span>
                <LuArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
